import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, Lock, User, Zap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { BrandLogo } from '@/components/brand/BrandLogo';
import { GoogleAuthButton } from '@/components/auth/GoogleAuthButton';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

export default function Signup() {
  const navigate = useNavigate();
  const { register, demoLogin } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setBusy(true);
    try {
      const u = await register(name || 'New User', email, password);
      toast.success(`Welcome, ${u.name.split(' ')[0]}!`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Sign up failed');
    } finally { setBusy(false); }
  };

  const handleDemo = async () => {
    setBusy(true);
    try {
      await demoLogin();
      toast.success('Demo session ready.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Demo login failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[hsl(var(--background))]">
      <div className="relative hidden lg:flex flex-col justify-between p-10 bg-[hsl(var(--surface-2))] overflow-hidden">
        <div className="absolute inset-0 hero-radial-light dark:hero-radial-dark" />
        <Link to="/" className="relative" data-testid="signup-logo-link">
          <BrandLogo height={52} testId="signup-brand" />
        </Link>
        <div className="relative max-w-md">
          <h2 className="font-display text-4xl font-semibold tracking-tight leading-tight">Stop guessing. Start optimizing.</h2>
          <p className="mt-3 text-muted-foreground">The average user earns 38% more rewards in the first 90 days.</p>
        </div>
        <div className="relative text-xs text-muted-foreground">Free during beta • No credit card required.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="flex justify-end mb-6"><ThemeToggle /></div>
          <Card className="p-8">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Create your account</h1>
            <p className="mt-1.5 text-sm text-muted-foreground">Takes less than a minute.</p>
            {/* Phase 16 — Google Sign-Up CTA. Same backend flow as
                Sign-In but the copy makes the intent clear so first-time
                users know they're creating an account, not signing into
                a non-existent one. */}
            <GoogleAuthButton
              mode="signup"
              disabled={busy}
              className="mt-6"
              testid="signup-google-button"
            />
            <Button
              onClick={handleDemo}
              disabled={busy}
              variant="ghost"
              className="w-full mt-3 gap-2 h-11 hover:bg-accent/10 text-accent"
              data-testid="signup-demo-button"
            >
              <Zap className="h-4 w-4" /> Skip and try demo
            </Button>
            <div className="my-6 flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-[11px] uppercase tracking-wider text-muted-foreground">or with email</span>
              <Separator className="flex-1" />
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="name">Full name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Aarav Mehta" className="pl-10 h-11" data-testid="signup-name-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11" data-testid="signup-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-10 h-11" data-testid="signup-password-input" />
                </div>
              </div>
              <div className="flex items-start gap-2.5 text-xs text-muted-foreground">
                <Checkbox id="tos" defaultChecked />
                <Label htmlFor="tos" className="text-xs font-normal leading-snug cursor-pointer">
                  I agree to the Terms and Privacy Policy. No card numbers stored.
                </Label>
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 gap-2" data-testid="signup-submit-button">
                Create account <ArrowRight className="h-4 w-4" />
              </Button>
            </form>
            <p className="mt-6 text-sm text-center text-muted-foreground">
              Already a member? <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
